/**
 * Restaurant utility functions for multi-tenant support
 * Provides helpers to get restaurantId and add it to documents/queries
 */

/**
 * Get the current restaurant ID from environment variables
 * @returns The restaurant ID for the current deployment
 * @throws Error if restaurantId is not configured
 */
export function getRestaurantId(): string {
  const restaurantId = import.meta.env.VITE_RESTAURANT_ID;
  
  if (!restaurantId) {
    const error = new Error('VITE_RESTAURANT_ID is not configured. Please ensure the build script generated the correct environment variables.');
    console.error('❌ Restaurant ID Error:', error.message);
    throw error;
  }
  
  return restaurantId;
}

/**
 * Validate that restaurantId exists (for safety checks)
 * @returns true if restaurantId is configured, false otherwise
 */
export function validateRestaurantId(): boolean {
  try {
    const restaurantId = import.meta.env.VITE_RESTAURANT_ID;
    return !!restaurantId;
  } catch {
    return false;
  }
}

/**
 * Add restaurantId to document data
 * @param data Document data object
 * @returns Document data with restaurantId added
 */
export function withRestaurantId<T extends Record<string, any>>(data: T): T & { restaurantId: string } {
  return {
    ...data,
    restaurantId: getRestaurantId(),
  };
}

/**
 * Add restaurantId filter to a Firestore query
 * This helper ensures all queries are scoped to the current restaurant
 * @param queryConstraints Existing query constraints array
 * @returns Query constraints with restaurantId filter added
 */
export function addRestaurantFilter(queryConstraints: any[]): any[] {
  const { where } = require('firebase/firestore');
  return [
    ...queryConstraints,
    where('restaurantId', '==', getRestaurantId()),
  ];
}

